import React from 'react';
import { List, Datagrid, TextField, EditButton, ReferenceInput, SelectInput, ReferenceField, FunctionField, SearchInput, usePermissions, BooleanField, AutocompleteInput } from 'react-admin';

// Components
import EmptyList from '../../components/misc/EmptyList';

// UI
import { Grid, Typography } from '@mui/material';

// Libs
import moment from 'moment';

// Utils
import { truncateString } from '../../utils/string';

// Entity
const entity = 'events';

const filters = [
  <SearchInput source="name" />,
  <ReferenceInput source="venue" reference="venues" sort={{ field: 'name', order: 'ASC' }} perPage={1000}>
    <AutocompleteInput
      optionText="name"
      fullWidth
      matchSuggestion={() => true}
      filterToQuery={(searchText) => ({ name: searchText })}
    />
  </ReferenceInput>,
  <ReferenceInput source="city" reference="cities">
    <SelectInput optionText={'name.en'} fullWidth />
  </ReferenceInput>,
  <ReferenceInput source="category" reference="categories">
    <SelectInput optionText={'name.en'} fullWidth />
  </ReferenceInput>,
  <SelectInput
    source="isActive"
    choices={[
      { id: true, name: 'Active' },
      { id: false, name: 'Inactive' },
    ]}
    label="Active Filter"
  />,
  <SelectInput
    source="isVerified"
    choices={[
      { id: true, name: 'Verified' },
      { id: false, name: 'Awaiting' },
    ]}
    label="Verified Filter"
  />,
  <ReferenceInput
    source="subcategory"
    reference="subcategories"
    sort={{ field: 'name.en', order: 'ASC' }}
  >
    <SelectInput optionText={'name.en'} fullWidth />
  </ReferenceInput>,
];

/**
 *
 * @returns Event List
 */
export const EventList = (props) => {
  const { permissions } = usePermissions();

  if (!permissions) return;

  let filter = { hasSessions: !true };

  if (permissions.crole && permissions.crole === 'organisationCompanyManager' && permissions.rid) filter.organisationCompanies = permissions.rid;
  if (permissions.crole && permissions.crole === 'venueManager' && permissions.rid) filter.venue = permissions.rid;

  let isAdmin = false;
  if (permissions.crole && permissions.crole === 'admin') isAdmin = true;

  let isOperator = false;
  if (permissions.crole && permissions.crole === 'operator') isOperator = true;

  let isEditor = false;
  if (permissions.crole && permissions.crole === 'editor') isEditor = true;

  if (permissions.crole && permissions.crole !== 'admin' && permissions.crole !== 'operator' && !permissions.rid)
    return (
      <EmptyList
        customMessage="You don't the right permission set to view this page. Please contact GişeKıbrıs if you believe this is an error."
        noCreate={true}
      />
    );

  // not allowed
  if (isEditor) window.location.href = '/#/news';

  if (isEditor) {
    return (
      <div style={{ flex: 1 }}>
        <Grid container spacing={0} style={{ background: '#FFF', padding: 30 }}>
          <Grid item xs={12} style={{ borderBottom: '1px solid #AAA', marginBottom: 20 }}>
            <Typography variant="h4" gutterBottom>
              Dashboard
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1" gutterBottom>
              You do not have permission to access this page.
            </Typography>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <List
      {...props}
      empty={<EmptyList entity={entity} />}
      sort={{ field: 'startdate', order: 'DESC' }}
      filters={filters}
      exporter={false}
      filter={filter}
      perPage={50}
    >
      <EventsDataGrid isAdmin={isAdmin} isOperator={isOperator} />
    </List>
  );
};

const EventsDataGrid = ({ isAdmin, isOperator }) => {
  return (
    <Datagrid bulkActionButtons={false}>
      <FunctionField
        source="name"
        sortable={true}
        sortBy="name"
        sortByOrder="ASC"
        render={(r) => truncateString(r.name, 30)}
      />

      <ReferenceField
        source="venue"
        reference="venues"
        link={false}
        sort={{ field: 'name', order: 'ASC' }}
        emptyText="—"
      >
        <FunctionField source="name" render={(r) => truncateString(r.name, 20)} />
      </ReferenceField>

      <ReferenceField source="category" reference="categories" link={false}>
        <TextField source="name.en" label={'Category'} />
      </ReferenceField>

      <FunctionField
        source="startdate"
        label="Date"
        render={(r) => `${moment(r.startdate).format('DD/MM/YY')}`}
      />
      <FunctionField
        source="startdate"
        label="Time"
        render={(r) => `${moment(r.startdate).format('LT')}`}
      />

      <BooleanField source="isActive" label="Active" />

      <FunctionField
        source="isVerified"
        label="Verified"
        render={(r) => {
          let bgColor = 'orange';
          let status = 'No';
          if (r.isVerified) {
            bgColor = 'green';
            status = 'Yes';
          }

          return (
            <div
              style={{ backgroundColor: bgColor, textAlign: 'center', borderRadius: 5, padding: 3 }}
            >
              <span style={{ color: 'white', textAlign: 'center' }}>{status}</span>
            </div>
          );
        }}
      />

      <FunctionField
        source="id"
        label="Status"
        render={(r) => {
          let bgColor = 'orange';
          let status = 'Upcoming';

          if (moment(r.startdate).isBefore(moment())) {
            bgColor = 'grey';
            status = 'Past';
          }

          if (r.isCancelled) {
            bgColor = 'red';
            status = 'Cancelled';
          }

          return (
            <div
              style={{ backgroundColor: bgColor, textAlign: 'center', borderRadius: 5, padding: 3 }}
            >
              <span style={{ color: 'white', textAlign: 'center' }}>{status}</span>
            </div>
          );
        }}
      />

      {!isOperator && (
        <FunctionField
          source="id"
          label="Stock Status"
          render={(r) => {
            return !r.hasSessions ? (
              <a href={`/#/events/${r.id}/show`}>Sales Report</a>
            ) : (
              'Main Session'
            );
          }}
        />
      )}

      {!isOperator && (
        <FunctionField
          source="sales"
          label="Sales"
          render={(r) => <a href={`/#/sales?eventID=${r.id}`}>View Sales</a>}
        />
      )}

      {!isOperator && (
        <FunctionField
          source="tickets"
          label="Tickets"
          render={(r) => <a href={`/#/tickets?eventID=${r.id}`}>View Tickets</a>}
        />
      )}

      <FunctionField
        source="id"
        label=""
        render={(r) => <EditButton />}
      />
    </Datagrid>
  );
};